import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import axios from 'axios';
import moment from 'moment';
import {
  descriptionCharactersLeft, isAnswered, missingTemplateFields, templateDefaultAnswers, templateForDocumentType, templateLines,
} from './NextgenTaskTemplateFields';

const sameId = (a, b) => String(a || '').toLowerCase() === String(b || '').toLowerCase();
const noAnalytics = () => {};

// NextGen task templates in the EHR drawer (ENG-3780). Loads the templates that
// apply to the user, applies the one for the picked document type (ticking
// Create Task when the drawer embeds tasks), lets the user pick or clear one,
// and holds the answers to its fields.
//
// Values go into the Task section's form fields only once that section is
// rendered: its fields are unmounted (preserve: false) while Create Task is off.
//
// onAnalytics(event, properties) reports template picks, automatic applies and
// clears, and first answers to a template field: ids and field types, never values.
//
// taskValues: the task fields' current values, so the user's entries survive a
// trigger field being cleared (which unmounts the task fields) and answered again.
const useNextgenTaskTemplates = ({
  enabled, credentialId, schema, embedsTask, taskRequested, taskSectionVisible, categoryOptions, priorityOptions,
  setTaskRequested, applyFieldValues, notes, taskValues, onAnalytics = noAnalytics,
}) => {
  const [templates, setTemplates] = useState([]);
  const [template, setTemplate] = useState(null);
  const [answers, setAnswers] = useState({});
  const [documentTypeId, setDocumentTypeId] = useState(null);
  const [pendingValues, setPendingValues] = useState(null);
  // 'auto' (from the document type) or 'manual' (picked or cleared by the user).
  const sourceRef = useRef(null);
  // Whether applying a template is what ticked Create Task.
  const autoTickedRef = useRef(false);
  const keptTaskValuesRef = useRef(null);
  const analyticsRef = useRef(onAnalytics);
  useEffect(() => {
    analyticsRef.current = onAnalytics;
  }, [onAnalytics]);
  const trackTemplate = useCallback((action, source, next) => {
    analyticsRef.current('nextgen_task_template_changed', { action, source, template_id: next?.id ?? null });
  }, []);

  useEffect(() => {
    setTemplates([]);
    setTemplate(null);
    setAnswers({});
    setPendingValues(null);
    sourceRef.current = null;
    autoTickedRef.current = false;
    if (!enabled || !credentialId) return undefined;

    let cancelled = false;
    axios.get('/api/v1/nextgen_task_templates/applicable', { params: { emr_integration_credential_id: credentialId } })
      .then((response) => { if (!cancelled) setTemplates(response.data.nextgen_task_templates || []); })
      // Templates are a convenience: without them the task is filled in by hand.
      .catch(() => {});
    return () => { cancelled = true; };
  }, [enabled, credentialId, schema]);

  const taskValuesFor = useCallback((next) => {
    const categoryKnown = (categoryOptions || []).some((option) => sameId(option.value, next.category_id));
    const priorityKnown = (priorityOptions || []).some((option) => sameId(option.value, next.priority));
    return {
      task_type: categoryKnown ? next.category_id : undefined,
      ...(priorityKnown && { task_priority: next.priority }),
      task_details: next.subject || undefined,
      task_due_date: next.due_in_days == null ? undefined : moment().startOf('day').add(next.due_in_days, 'days'),
    };
  }, [categoryOptions, priorityOptions]);

  const apply = useCallback((next, source) => {
    sourceRef.current = source;
    setTemplate(next);
    setAnswers(templateDefaultAnswers(next));
    keptTaskValuesRef.current = null;
    // A removed template's task values must not land when the task fields return.
    if (!next) {
      setPendingValues(null);
      return;
    }

    if (embedsTask && !taskRequested) {
      autoTickedRef.current = source === 'auto';
      setTaskRequested(true);
    }
    setPendingValues(taskValuesFor(next));
  }, [embedsTask, setTaskRequested, taskRequested, taskValuesFor]);

  // A template with a trigger field (Advanced settings) makes a task only once
  // that field is answered; until then the drawer files the document alone and
  // shows just the template's own fields.
  const triggerField = template?.trigger_field_key
    ? template.fields.find((field) => field.key === template.trigger_field_key)
    : null;
  const taskSkipped = embedsTask && Boolean(triggerField) && !isAnswered(answers[triggerField.key]);
  const taskFieldsShown = taskSectionVisible && !taskSkipped;
  const shownBeforeRef = useRef(taskFieldsShown);
  // The task values last seen while the fields were shown, and the copy kept when
  // a cleared trigger hid them.
  const lastShownValuesRef = useRef(null);

  useEffect(() => {
    if (taskFieldsShown) lastShownValuesRef.current = taskValues;
  }, [taskFieldsShown, taskValues]);

  useEffect(() => {
    // The task fields unmount while skipped, so they are filled again when they
    // return: with what the user had entered, or else from the template.
    if (!taskFieldsShown && shownBeforeRef.current && taskSkipped) keptTaskValuesRef.current = lastShownValuesRef.current;
    if (taskFieldsShown && !shownBeforeRef.current && template && !pendingValues) {
      setPendingValues(keptTaskValuesRef.current || taskValuesFor(template));
      keptTaskValuesRef.current = null;
    }
    shownBeforeRef.current = taskFieldsShown;
  }, [pendingValues, taskFieldsShown, taskSkipped, taskValuesFor, template]);

  useEffect(() => {
    if (!pendingValues || !taskFieldsShown) return;
    applyFieldValues(pendingValues);
    setPendingValues(null);
  }, [applyFieldValues, pendingValues, taskFieldsShown]);

  // Follow the document type until the user picks or clears a template.
  useEffect(() => {
    if (sourceRef.current === 'manual') return;
    const match = templateForDocumentType(templates, documentTypeId);
    if (match) {
      if (match.id !== template?.id) {
        apply(match, 'auto');
        trackTemplate('applied', 'document_type', match);
      }
      return;
    }
    if (sourceRef.current !== 'auto') return;

    trackTemplate('cleared', 'document_type', template);
    apply(null, null);
    if (autoTickedRef.current) setTaskRequested(false);
    autoTickedRef.current = false;
    // template is read only to skip re-applying the same one.
  }, [documentTypeId, templates]);

  const selectTemplate = useCallback((id) => {
    autoTickedRef.current = false;
    const next = templates.find((option) => id != null && String(option.id) === String(id)) || null;
    trackTemplate(next ? 'applied' : 'cleared', 'picker', next || template);
    apply(next, 'manual');
  }, [apply, template, templates, trackTemplate]);

  // Unticking Create Task drops the template; ticking it again re-applies the
  // document type's template unless the user chose otherwise.
  const taskToggled = useCallback((checked) => {
    autoTickedRef.current = false;
    if (!checked) {
      if (sourceRef.current !== 'manual') sourceRef.current = null;
      setTemplate(null);
      setAnswers({});
      setPendingValues(null);
      return;
    }
    if (sourceRef.current === 'manual') return;
    const match = templateForDocumentType(templates, documentTypeId);
    if (match) apply(match, 'auto');
  }, [apply, documentTypeId, templates]);

  const setAnswer = useCallback((key, answer) => {
    // Once per field as it goes from unanswered to answered, not per keystroke.
    if (!isAnswered(answers[key]) && isAnswered(answer)) {
      const field = template?.fields?.find((option) => option.key === key);
      analyticsRef.current('nextgen_task_template_field_answered', { template_id: template?.id ?? null, field_type: field?.type || null });
    }
    setAnswers((previous) => {
      const next = { ...previous };
      if (answer) next[key] = answer;
      else delete next[key];
      return next;
    });
  }, [answers, template]);

  const lines = useMemo(() => templateLines(template, answers), [template, answers]);
  const missing = useMemo(() => {
    const labels = missingTemplateFields(template, answers);
    // Standalone Create Task always makes a task, so its trigger field is required.
    const triggerMissing = !embedsTask && triggerField && !isAnswered(answers[triggerField.key]);
    return triggerMissing ? [...labels, triggerField.label] : labels;
  }, [answers, embedsTask, template, triggerField]);
  const charactersLeft = descriptionCharactersLeft(lines, notes);
  const categoryUnavailable = Boolean(template?.category_id)
    && (categoryOptions || []).length > 0
    && !categoryOptions.some((option) => sameId(option.value, template.category_id));

  return {
    templates,
    template,
    answers,
    setAnswer,
    selectTemplate,
    taskToggled,
    setDocumentTypeId,
    lines,
    missing,
    charactersLeft,
    categoryUnavailable,
    triggerField,
    taskSkipped,
    blocked: Boolean(template) && !taskSkipped && (missing.length > 0 || charactersLeft < 0),
  };
};

export default useNextgenTaskTemplates;
